import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('click', this.#handleShowMoreClick, { signal });
    window.addEventListener('resize', this.#updateShowMoreState, { signal });
    this.#updateShowMoreState();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * The gallery waiting to replace this one, or null when no variant update is in progress.
   * @type {Element | null}
   */
  #incomingGallery = null;

  /**
   * Images preloaded for the incoming gallery. Kept here so they stay in memory until it is inserted.
   * @type {HTMLImageElement[]}
   */
  #preloadedImages = [];

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * The swap waits for the leading images of the new gallery, so a variant with its own
   * photos does not show an empty frame while they download.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.#incomingGallery = newMediaGallery;
    this.#leadingImagesLoaded(newMediaGallery).then(() => {
      // A newer variant update took over, or this gallery has already been replaced.
      if (this.#incomingGallery !== newMediaGallery || !this.isConnected) return;

      this.replaceWith(newMediaGallery);
    });
  };

  /**
   * Resolves when the images shown first in the given gallery are loaded, or after a short
   * timeout so a slow connection never blocks the variant change.
   *
   * @param {Element} gallery - The gallery that is about to be inserted.
   * @returns {Promise<unknown>}
   */
  #leadingImagesLoaded(gallery) {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const grid =
      !isMobile && gallery.getAttribute('data-presentation') === 'grid'
        ? gallery.querySelector('.media-gallery__grid')
        : null;
    const images = Array.from((grid ?? gallery).querySelectorAll('img')).slice(0, grid ? 3 : 1);

    this.#preloadedImages = images.map((image) => {
      // The same sizes and srcset make the browser pick the file the inserted image will use.
      const preload = new Image();
      const sizes = image.getAttribute('sizes');
      const srcset = image.getAttribute('srcset');
      const src = image.getAttribute('src');

      if (sizes) preload.sizes = sizes;
      if (srcset) preload.srcset = srcset;
      if (src) preload.src = src;

      return preload;
    });

    const loaded = Promise.all(this.#preloadedImages.map((preload) => preload.decode().catch(() => undefined)));
    const timeout = new Promise((resolve) => setTimeout(resolve, 1500));

    return Promise.race([loaded, timeout]);
  }

  /**
   * Handles clicking the "show more media" button.
   *
   * @param {Event} event
   */
  #handleShowMoreClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target : null);
    const button = target?.closest('[data-media-show-more-button]');
    if (!button || !this.contains(button)) return;

    const grid = this.querySelector('.media-gallery__grid[data-show-more-enabled="true"]');
    if (!grid) return;

    grid.classList.add('is-expanded');
    this.#updateShowMoreState();
  };

  /**
   * Updates visibility for the "show more media" button wrapper on desktop/mobile.
   */
  #updateShowMoreState = () => {
    const grid = this.querySelector('.media-gallery__grid[data-show-more-enabled="true"]');
    const buttonWrap = this.querySelector('[data-media-show-more-wrap]');
    if (!grid || !buttonWrap) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      buttonWrap.hidden = true;
      buttonWrap.style.display = 'none';
      return;
    }

    const isExpanded = grid.classList.contains('is-expanded');
    buttonWrap.hidden = isExpanded;
    buttonWrap.style.display = isExpanded ? 'none' : '';
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (this.dataset.zoomMobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    if (this.dataset.zoomMobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
